import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mail, ShieldCheck, LockKeyhole, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Email-based Account Recovery — replaces the retired SSN/DL/passport form.
 *
 * Two modes:
 *   1. no `?token=` in the URL  → request form (enter email, receive link).
 *   2. `?token=<jwt>` in the URL → confirm form (set a new password).
 *
 * After a successful confirm the user is redirected to `/login` where they
 * sign in with the new password and are prompted to re-enrol TOTP 2FA.
 */
export default function Recovery() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  // ---------------- REQUEST MODE ---------------- //
  const [email, setEmail] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [requestDone, setRequestDone] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");

  const requestLink = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Enter your account email.");
    setRequesting(true);
    try {
      const { data } = await api.post("/auth/recover/request", { email });
      setRequestMessage(data.message || "If an account exists, a recovery email has been sent.");
      setRequestDone(true);
      toast.success("Recovery email sent (if the account exists).");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to send recovery email.");
    } finally {
      setRequesting(false);
    }
  };

  // ---------------- CONFIRM MODE ---------------- //
  const [validating, setValidating] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenError, setTokenError] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    setValidating(true);
    (async () => {
      try {
        const { data } = await api.get(`/auth/recover/token-info?token=${encodeURIComponent(token)}`);
        if (!data.valid) {
          setTokenError(
            data.reason === "invalid_or_expired"
              ? "This recovery link is invalid or has expired. Request a new one below."
              : "This recovery link is no longer valid (it may already have been used). Request a new one below."
          );
        } else {
          setTokenInfo(data);
        }
      } catch (_e) {
        setTokenError("Unable to validate the recovery link. Try again.");
      } finally {
        setValidating(false);
      }
    })();
  }, [token]);

  const confirmRecover = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) return toast.error("Password must be at least 8 characters.");
    if (newPassword !== confirmPassword) return toast.error("Passwords do not match.");
    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/auth/recover/confirm",
        { new_password: newPassword },
        { headers: { "X-Recover-Token": token } }
      );
      toast.success(data.message || "Account recovered.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Recovery failed.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------- RENDER ---------------- //
  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center p-6">
      <div
        data-testid="recover-panel"
        className="max-w-md w-full bg-white border border-brand rounded-2xl p-8 shadow-lg"
      >
        <button
          onClick={() => navigate("/login")}
          className="text-sm text-brand-primary flex items-center gap-1 mb-4 hover:underline"
          data-testid="recover-back-to-login"
        >
          <ArrowLeft size={14} /> Back to sign in
        </button>

        {/* ---- CONFIRM MODE ---- */}
        {token && (
          <>
            <div className="flex items-center gap-2 mb-2">
              <LockKeyhole className="text-brand-primary" size={22} />
              <h1 className="text-xl font-semibold">Set a new password</h1>
            </div>

            {validating && (
              <p className="text-sm text-slate-600" data-testid="recover-validating">
                Validating recovery link…
              </p>
            )}

            {!validating && tokenError && (
              <div data-testid="recover-token-error" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 mb-4">
                {tokenError}
              </div>
            )}

            {!validating && tokenInfo && (
              <>
                <p className="text-sm text-slate-600 mb-4">
                  Recovering access to <strong>{tokenInfo.email_masked}</strong>. This action will:
                </p>
                <ul className="text-xs text-slate-500 mb-4 pl-4 list-disc space-y-1">
                  <li>Reset your password to the value you enter below.</li>
                  <li>Clear your current two-factor authenticator — you'll re-enrol at next login.</li>
                  <li>Sign out every existing device.</li>
                  <li>Generate a fresh set of backup recovery codes when you finish 2FA setup.</li>
                </ul>

                <form onSubmit={confirmRecover} className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-slate-700">New password</label>
                    <input
                      type="password"
                      data-testid="recover-new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                      minLength={8}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-700">Confirm new password</label>
                    <input
                      type="password"
                      data-testid="recover-confirm-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                      placeholder="Retype the password"
                      autoComplete="new-password"
                      minLength={8}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="recover-confirm-submit"
                    className="w-full bg-brand-primary text-white font-semibold rounded-lg px-4 py-2.5 hover:bg-brand-primary/90 disabled:opacity-60"
                  >
                    {submitting ? "Recovering…" : "Complete recovery"}
                  </button>
                </form>
              </>
            )}
          </>
        )}

        {/* ---- REQUEST MODE ---- */}
        {!token && !requestDone && (
          <>
            <div className="flex items-center gap-2 mb-2">
              <Mail className="text-brand-primary" size={22} />
              <h1 className="text-xl font-semibold">Recover your account</h1>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Enter your account email. If a matching account exists, we'll send you a
              single-use recovery link that expires in 30 minutes.
            </p>
            <form onSubmit={requestLink} className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-700">Account email</label>
                <input
                  type="email"
                  data-testid="recover-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={requesting}
                data-testid="recover-request-submit"
                className="w-full bg-brand-primary text-white font-semibold rounded-lg px-4 py-2.5 hover:bg-brand-primary/90 disabled:opacity-60"
              >
                {requesting ? "Sending…" : "Send recovery link"}
              </button>
            </form>
            <p className="text-xs text-slate-500 mt-4">
              This form never collects government-issued IDs. All you need is
              access to the email on file.
            </p>
          </>
        )}

        {!token && requestDone && (
          <>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="text-emerald-600" size={22} />
              <h1 className="text-xl font-semibold">Check your email</h1>
            </div>
            <p
              data-testid="recover-request-confirmation"
              className="text-sm text-slate-600"
            >
              {requestMessage}
            </p>
            <p className="text-xs text-slate-500 mt-3">
              Nothing about your account has changed yet. If you don't recognise this
              request, you can ignore the email safely.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
